/**
 * How many of a building's units carry a county case-register signal — an
 * unsafe-structure or code case, a violation lien, an expired permit — and
 * what share of the building that is.
 *
 * **The share exists only where a live register reaches the building**:
 * Miami-Dade, and unincorporated Hillsborough. Everywhere else the engine
 * leaves `distress_rate` null on purpose (see its county modules), because
 * a 0% there would read as "checked, and clean" when it means no register we
 * read covers these units. So a zero beside a null share is said in words —
 * the drawer once printed "0 of 3 · —", which is both claims at once.
 */

import type { Building } from "../api/types";
import { fmt, pct } from "./format";

type Counts = Pick<Building, "distressed_units" | "total_units" | "distress_rate">;

export const CASE_UNITS_LABEL = "Units with a case or lien";

export interface CaseUnits {
  value: string;
  /** True when the value is a sentence rather than a count. */
  words: boolean;
}

export function caseUnitsValue(building: Counts): CaseUnits {
  const { distressed_units: count, total_units: total, distress_rate: rate } = building;
  if (rate !== null) {
    return { value: `${count} of ${fmt(total)} · ${pct(rate)}`, words: false };
  }
  // A register frozen in the past (Pinellas's, Volusia's) still names units:
  // the count stands, the share is not claimed.
  if (count > 0) {
    return { value: total ? `${count} of ${fmt(total)}` : `${count}`, words: false };
  }
  return { value: NO_CASE_REGISTER, words: true };
}

export const NO_CASE_REGISTER = "No current case register reaches this building";

/** What a case cell says where the building's unit cell says it in words. */
export const CASE_NOT_COVERED = "Not covered";

/**
 * One case count — unsafe structure, expired permits, violation liens — by
 * the same rule. The engine writes 0 where no register reaches (its column
 * contract needs every key), so beside "No current case register reaches
 * this building" a 0 would be the clean record the sentence just denied.
 * A count above zero always stands: it came from a register.
 */
export function caseCountValue(building: Counts, count: number): CaseUnits {
  if (count === 0 && caseUnitsValue(building).words) {
    return { value: CASE_NOT_COVERED, words: true };
  }
  return { value: String(count), words: false };
}
